import { useState } from 'react';
import { useListFetch, useRequest } from '../../../../../hooks';
import { notify } from '../../../../../app';
import { getKnowledgeSourceChunks, searchKnowledge } from '../../../../../api';
import type { SearchKnowledgeResponse } from '../../../../../api';
import { useRouteMatch } from 'react-router-dom';

export type FormProps = {
  search: string | null;
  limit?: number;
  threshold?: number;
};

const defaultFormValues: FormProps = {
  search: '',
  limit: 3,
  threshold: 0,
};

export const useKnowledgeSourceDetails = (dataSourceId: string = '') => {
  const [form, setForm] = useState<FormProps>(defaultFormValues);
  const [pageDetails, setPageDetails] = useState(1);
  const [searchResult, setSearchResult] =
    useState<SearchKnowledgeResponse | null>(null);
  const { params } = useRouteMatch<{ id: string }>();

  const {
    nodes: dataChunks,
    filters,
    setFilters,
    setSort,
    intializing: loadingChunks,
    refresh,
    ...rest
  } = useListFetch(
    async queryParams => {
      if (!dataSourceId) return;

      return await getKnowledgeSourceChunks({
        knowledgeBaseId: params?.id || '',
        dataSourceId,
        page: pageDetails,
        limit: queryParams?.pagination?.limit || 20,
      });
    },
    {
      deps: [params?.id, dataSourceId, pageDetails],
      resultKey: 'chunks',
      withQueryParams: true,
    }
  );

  const [handleSearchKnowledgeRequest, searchingKnowledge] = useRequest(
    _signal => {
      return searchKnowledge({
        query: form.search || '',
        knowledge_base_id: params?.id || '',
        num_results: form.limit || 3,
        threshold: form.threshold || 0,
      });
    },
    {
      onSuccess: data => {
        setSearchResult(data);
      },
      onError: () => {
        notify.error('Failed to search the knowledge');
      },
      deps: [],
    }
  );

  const resetSearch = () => {
    setForm(defaultFormValues);
    setSearchResult(null);
  };

  return {
    handleSearchKnowledge: handleSearchKnowledgeRequest,
    searchingKnowledge,
    searchResult,
    setForm,
    form,
    dataChunks,
    filters,
    setFilters,
    setSort,
    pageDetails,
    setPageDetails,
    loadingChunks,
    refresh,
    resetSearch,
    ...rest,
  };
};
